import { forwardRef, useEffect, useImperativeHandle } from "react";
import { DataTable } from "primereact/datatable";
import { useAPIGetReportCustomerVoucher } from "../api/api-get-report-customer-voucher";
import { Column } from "primereact/column";
import ReportCustomerVoucherFilter from "./ReportCustomerVoucherFilter";
import { Paginator } from "primereact/paginator";
import { format, differenceInDays } from "date-fns";

const ReportCustomerVoucherList = forwardRef((props, ref) => {
  const { onClickRowDelete, onClickRowEdit, onClickAddNew } = props;

  const {
    loading,
    listData,
    fetchListData,
    filterValue,
    setFilterValue,
    getFilter,
    handleOnClickRefresh,
    handleOnPage,
    paging,
    handleOnUpdateViewRow,
    handleOnDeleteViewRow,
  } = useAPIGetReportCustomerVoucher();

  useImperativeHandle(ref, () => ({
    handleOnUpdateViewRow: (row) => handleOnUpdateViewRow(row),
    handleOnDeleteViewRow: (row) => handleOnDeleteViewRow(row),
  }));

  useEffect(() => {
    fetchListData({ page: paging.page, take: paging.take });
  }, []);

  // Body templates

  const customerBodyTemplate = (row) => {
    const { data_customer } = row;
    return (
      <div className="col-start-start gap-1">
        <span className="font-semibold text-slate-700">
          {data_customer?.name}
        </span>
        <span className="text-xs text-slate-500">
          {data_customer?.code}
        </span>
        <span className="text-xs font-semibold text-blue-600">
          BC: {data_customer?.data_bc?.name ?? "-"}
        </span>
      </div>
    );
  };

  const voucherBodyTemplate = (row) => {
    const { amount } = row;
    const used = row.sales_payment_id !== null && row.sales_payment_id !== undefined;
    const amountStr = new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount ?? 0);
    return (
      <div className="col-start-start gap-1 w-full">
        <div className="row-between-center gap-2 w-full">
          <span className="font-semibold text-slate-700 text-sm">{row.name}</span>
          <span
            className={`px-2 py-0.5 rounded-full text-xs font-bold ${used
              ? "bg-green-100 text-green-700 border border-green-300"
              : "bg-slate-100 text-slate-500 border border-slate-300"
              }`}
          >
            {used ? "Used" : "Not Used"}
          </span>
        </div>
        <span className="text-xs text-slate-500">Amount: {amountStr}</span>
        {row.description && (
          <span className="text-xs text-gray-500">{row.description}</span>
        )}
      </div>
    );
  };

  const expiredBodyTemplate = (row) => {
    const { expired_date } = row;
    const used = row.sales_payment_id !== null && row.sales_payment_id !== undefined;
    const txDate = row.data_sales_payment?.transaction_date;
    if (!expired_date) return "-";
    const today = new Date();
    const expDate = new Date(expired_date);
    const daysLeft = differenceInDays(expDate, today);
    const isExpired = daysLeft < 0;
    const isNear = daysLeft >= 0 && daysLeft <= 30;
    const suppressExpiredStyle = used && isExpired;
    return (
      <div className="col-start-start gap-1">
        <span
          className={`text-sm font-semibold ${suppressExpiredStyle
            ? "text-slate-700"
            : isExpired
              ? "text-red-600"
              : isNear
                ? "text-orange-500"
                : "text-slate-700"
            }`}
        >
          {format(expDate, "dd MMM yyyy")}
        </span>
        {!suppressExpiredStyle && (
          <span
            className={`text-xs ${isExpired
              ? "text-red-500"
              : isNear
                ? "text-orange-400"
                : "text-slate-400"
              }`}
          >
            {isExpired
              ? `Expired ${Math.abs(daysLeft)} days ago`
              : daysLeft === 0
                ? "Expires today!"
                : `${daysLeft} days left`}
          </span>
        )}
        {txDate && (
          <span className="text-xs text-slate-400">
            Used: {format(new Date(txDate), "dd MMM yyyy HH:mm")}
          </span>
        )}
      </div>
    );
  };

  const approvedBodyTemplate = (row) => {
    const { data_verif_owner, verif_owner_datetime } = row;
    if (!data_verif_owner) return "-";
    return (
      <div className="col-start-start gap-1">
        <span className="font-semibold text-green-700">{data_verif_owner?.name}</span>
        <span className="text-xs text-slate-400">
          {verif_owner_datetime
            ? format(new Date(verif_owner_datetime), "dd MMM yyyy HH:mm")
            : "-"}
        </span>
      </div>
    );
  };

  return (
    <div className="col-start-start w-full gap-4">
      {/* filter section */}
      <div className="elevated-card-tight-vertical row-start-start w-full gap-4">
        <ReportCustomerVoucherFilter
          filterValue={filterValue}
          setFilterValue={setFilterValue}
          onClickRefresh={() => {
            const filter = getFilter(filterValue);
            fetchListData({ page: 1, take: paging.take, filter });
          }}
        />
      </div>

      {/* table section */}
      <div className="elevated-card-tight-vertical col-start-center w-full gap-2">
        <DataTable
          dataKey="id"
          className="w-100"
          loading={loading}
          showGridlines
          stripedRows
          value={listData}
        >
          <Column
            field="no"
            header="#"
            className="text-center"
            headerClassName="w-[4rem]"
            alignHeader="center"
            bodyStyle={{ textAlign: "center" }}
          />
          <Column
            field="data_customer.name"
            header="Customer"
            body={customerBodyTemplate}
          />
          <Column
            field="name"
            header="Voucher"
            body={voucherBodyTemplate}
          />
          <Column
            field="expired_date"
            header="Expired / Used Date"
            body={expiredBodyTemplate}
          />
          <Column
            field="data_verif_owner.name"
            header="Approved By"
            body={approvedBodyTemplate}
          />
        </DataTable>
        <Paginator
          first={parseInt(paging.take) * (paging.page - 1)}
          rows={parseInt(paging.take)}
          totalRecords={parseInt(paging.total)}
          rowsPerPageOptions={[5, 10, 20, 50]}
          onPageChange={handleOnPage}
        />
      </div>
    </div>
  );
});

export default ReportCustomerVoucherList;
